/**
 * Single source of SEO metadata, shared by the runtime <Seo> component
 * (src/components/Seo.tsx) and the build-time prerender (scripts/prerender.ts).
 *
 * Pure data + pure builders only: no React, no Vite env, no Supabase, no CSS —
 * that is what lets the prerender script (plain `tsx`) import it.
 *
 * ponytail: canonical origin is https://www.coliviq.de — the live production
 * host. The apex coliviq.de 307-redirects to www; coliviq.com does not resolve.
 * Keep this in sync with index.html's static tags and public/robots.txt.
 */

export const SITE = {
  origin: "https://www.coliviq.de",
  name: "coliviq",
  twitter: "@coliviq",
  ogImage: "/coliviq-social-image.png",
  /** The site renders German by default (DEFAULT_LANGUAGE); English is a toggle. */
  defaultLang: "de" as const,
} as const;

export type SeoLang = "de" | "en";

export type SeoMeta = {
  title: string;
  description: string;
  /** Absolute canonical URL. */
  canonical: string;
  lang: SeoLang;
  /** Absolute OG/Twitter image URL. */
  image: string;
  /** JSON-LD blocks to embed (already plain objects). */
  jsonLd?: Record<string, unknown>[];
  /** "noindex,follow" etc. Omitted = indexable. */
  robots?: string;
};

/** Join an app path onto the canonical origin. */
export function absoluteUrl(pathname: string): string {
  if (/^https?:\/\//.test(pathname)) return pathname;
  return SITE.origin + (pathname.startsWith("/") ? pathname : "/" + pathname);
}

type Localized = Record<SeoLang, { title: string; description: string }>;

/**
 * Static marketing routes, mirror of src/App.tsx. German is the canonical
 * (prerendered) copy; English feeds the runtime language toggle.
 */
export const STATIC_ROUTE_META: Record<string, Localized> = {
  "/": {
    de: {
      title: "coliviq – Möbliertes Wohnen auf Zeit in modernen WGs",
      description:
        "Möblierte Zimmer in gepflegten Co-Living-WGs in München, Augsburg und am Starnberger See. 100 % digital, mit Anmeldung, ohne Makler.",
    },
    en: {
      title: "coliviq – Furnished Co-Living Rooms in Germany",
      description:
        "Fully furnished rooms in modern co-living communities around Munich and Augsburg. 100% digital, with Anmeldung, no broker fees.",
    },
  },
  "/concept": {
    de: {
      title: "Konzept – So funktioniert Co-Living bei coliviq",
      description:
        "Vollmöbliert, all-inclusive und digital: wie Wohnen auf Zeit in einer coliviq-WG abläuft – vom Zimmer bis zur Anmeldung.",
    },
    en: {
      title: "Concept – How Co-Living Works at coliviq",
      description:
        "Fully furnished, all-inclusive and digital: how living in a coliviq co-living community works, from the room to your Anmeldung.",
    },
  },
  "/rooms": {
    de: {
      title: "Möblierte Zimmer & WG-Zimmer finden | coliviq",
      description:
        "Freie möblierte Zimmer in coliviq-WGs – mit Anmeldung, all-inclusive und sofort oder ab Wunschtermin verfügbar.",
    },
    en: {
      title: "Find Furnished Rooms & Co-Living Spaces | coliviq",
      description:
        "Browse available furnished rooms in coliviq communities – with Anmeldung, all-inclusive, available now or from your move-in date.",
    },
  },
  "/community": {
    de: {
      title: "Community – Leben in einer coliviq-WG",
      description:
        "Mehr als ein Zimmer: Events, gemeinsame Flächen und eine echte Community in jeder coliviq-WG.",
    },
    en: {
      title: "Community – Life in a coliviq Co-Living Home",
      description:
        "More than a room: events, shared spaces and a real community in every coliviq co-living home.",
    },
  },
  "/careers": {
    de: {
      title: "Karriere bei coliviq",
      description:
        "Werde Teil von coliviq und gestalte modernes Wohnen auf Zeit in Deutschland mit.",
    },
    en: {
      title: "Careers at coliviq",
      description:
        "Join coliviq and help shape modern furnished co-living across Germany.",
    },
  },
  "/contact": {
    de: {
      title: "Kontakt | coliviq",
      description:
        "Fragen zu möblierten Zimmern, Anmeldung oder Verwaltung? Kontaktiere das coliviq-Team.",
    },
    en: {
      title: "Contact | coliviq",
      description:
        "Questions about furnished rooms, Anmeldung or property management? Get in touch with the coliviq team.",
    },
  },
  "/developers": {
    de: {
      title: "Wohnung vermieten lassen – Vollverwaltung | coliviq",
      description:
        "Sorgenfrei möbliert vermieten: coliviq übernimmt die Vollverwaltung, sichert gegen Mietausfall ab und vermeidet Leerstand.",
    },
    en: {
      title: "Let coliviq Manage Your Property – Full Service",
      description:
        "Rent out your furnished property worry-free: coliviq handles full management, protects against rent loss and avoids vacancy.",
    },
  },
  "/owners": {
    de: {
      title: "Co-Living- & WG-Verwaltungssoftware für Betreiber | coliviq",
      description:
        "Hausverwaltung online: coliviq-Software für Co-Living-Betreiber – Verträge, SEPA-Lastschrift und Abrechnung an einem Ort.",
    },
    en: {
      title: "Co-Living & Property Management Software for Operators",
      description:
        "Manage your co-living operation online: contracts, SEPA direct debit and billing in one place with coliviq software.",
    },
  },
  "/imprint": {
    de: {
      title: "Impressum | coliviq",
      description: "Impressum und Anbieterkennzeichnung von coliviq.",
    },
    en: {
      title: "Imprint | coliviq",
      description: "Legal notice and provider information for coliviq.",
    },
  },
  "/privacy-policy": {
    de: {
      title: "Datenschutzerklärung | coliviq",
      description: "Wie coliviq mit deinen personenbezogenen Daten umgeht.",
    },
    en: {
      title: "Privacy Policy | coliviq",
      description: "How coliviq handles your personal data.",
    },
  },
  "/terms-of-service": {
    de: {
      title: "AGB | coliviq",
      description: "Allgemeine Geschäftsbedingungen von coliviq.",
    },
    en: {
      title: "Terms of Service | coliviq",
      description: "coliviq's terms of service.",
    },
  },
};

/** Build the SeoMeta for a known static route; null if the route isn't listed. */
export function staticRouteMeta(
  pathname: string,
  lang: SeoLang = SITE.defaultLang
): SeoMeta | null {
  const entry = STATIC_ROUTE_META[pathname];
  if (!entry) return null;
  const copy = entry[lang] ?? entry[SITE.defaultLang];
  return {
    title: copy.title,
    description: copy.description,
    canonical: absoluteUrl(pathname),
    lang,
    image: absoluteUrl(SITE.ogImage),
  };
}

// ---- JSON-LD builders -----------------------------------------------------

export function organizationLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE.name,
    url: SITE.origin,
    logo: absoluteUrl("/app-icon.png"),
    sameAs: ["https://instagram.com/coliviq"],
  };
}

export function websiteLd(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    url: SITE.origin,
  };
}

export type RoomLd = {
  id: string | number;
  title: string;
  city?: string | null;
  description?: string | null;
  price?: number | null;
  available?: boolean | null;
  image?: string | null;
};

/** schema.org Accommodation + Offer for a single room page. */
export function accommodationLd(room: RoomLd): Record<string, unknown> {
  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Accommodation",
    name: room.title,
    url: absoluteUrl(`/property/${room.id}`),
  };
  if (room.description) ld.description = room.description;
  if (room.city)
    ld.address = { "@type": "PostalAddress", addressLocality: room.city };
  if (room.image) ld.image = absoluteUrl(room.image);
  if (typeof room.price === "number" && room.price > 0) {
    ld.offers = {
      "@type": "Offer",
      price: room.price,
      priceCurrency: "EUR",
      availability: room.available
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
    };
  }
  return ld;
}

/** ItemList of rooms for a city page. */
export function cityItemListLd(
  city: string,
  rooms: RoomLd[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `Möblierte Zimmer in ${city}`,
    itemListElement: rooms.map((room, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: absoluteUrl(`/property/${room.id}`),
      name: room.title,
    })),
  };
}

export function faqLd(
  items: { question: string; answer: string }[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.question,
      acceptedAnswer: { "@type": "Answer", text: it.answer },
    })),
  };
}

// ---- Keyword landing pages (semi-programmatic SEO) ------------------------
// City × intent pages mapped to Neel's keyword list. One row = one indexable
// page: KeywordLanding.tsx renders it at runtime, prerender.ts bakes its meta +
// FAQPage JSON-LD and adds it to the sitemap, and App.tsx generates its route.
// German-first; EN counterparts + hreflang are a documented follow-up (add rows
// with lang:"en"). `city` must be a published DB city — the CTA funnels into
// /rooms/<city slug> (slug = city.toLowerCase(), matching Properties.tsx).

export type KeywordPage = {
  /** Canonical path, also the route and the slug, e.g. "/wg-zimmer-muenchen". */
  path: string;
  lang: SeoLang;
  /** Published DB city this page funnels into. */
  city: string;
  title: string;
  description: string;
  h1: string;
  /** Intro paragraphs, rendered verbatim as <p>. */
  intro: string[];
  benefits: string[];
  /** Rendered as an accordion AND emitted as FAQPage JSON-LD (GEO fuel). */
  faq: { question: string; answer: string }[];
  ctaLabel: string;
};

export const KEYWORD_PAGES: KeywordPage[] = [
  {
    path: "/wg-zimmer-muenchen",
    lang: "de",
    city: "München",
    title: "WG-Zimmer München – möbliert & mit Anmeldung | coliviq",
    description:
      "Freie WG-Zimmer in München: vollmöbliert, all-inclusive und mit Anmeldung. Ohne Makler, 100 % digital – jetzt freies Zimmer in München finden.",
    h1: "WG-Zimmer in München",
    intro: [
      "Du suchst ein WG-Zimmer in München? Bei coliviq findest du vollmöblierte Zimmer in gepflegten Co-Living-WGs – all-inclusive, mit Anmeldung und ohne Maklergebühr.",
      "Strom, Internet und Nebenkosten sind in der Miete enthalten. Du ziehst ein, meldest dich an und lebst sofort los – komplett digital, ohne Besichtigungsmarathon.",
    ],
    benefits: [
      "Vollmöbliert – Bett, Schreibtisch und Küche sind startklar",
      "All-inclusive: Strom, Internet und Nebenkosten in einer Miete",
      "Mit Anmeldung – inklusive Wohnungsgeberbestätigung fürs Bürgerbüro",
      "Ohne Makler und ohne versteckte Gebühren",
      "100 % digitaler Vertrag – in Minuten unterschrieben",
    ],
    faq: [
      {
        question:
          "Bekomme ich in einem coliviq-WG-Zimmer in München eine Anmeldung?",
        answer:
          "Ja. Zu jedem Zimmer erhältst du eine Wohnungsgeberbestätigung, mit der du dich beim Bürgerbüro in München anmelden kannst.",
      },
      {
        question: "Sind die WG-Zimmer in München möbliert?",
        answer:
          "Alle Zimmer sind vollmöbliert und die Gemeinschaftsküche ist komplett ausgestattet. Du brauchst nur deinen Koffer.",
      },
      {
        question: "Fallen Maklergebühren an?",
        answer:
          "Nein. Du mietest direkt über coliviq – ohne Makler und ohne Provision.",
      },
      {
        question: "Was ist in der Miete enthalten?",
        answer:
          "Die Miete ist all-inclusive: Strom, Heizung, Wasser und Internet sind enthalten, abgerechnet in einem Betrag.",
      },
    ],
    ctaLabel: "Freie WG-Zimmer in München ansehen",
  },
  {
    path: "/moebliertes-zimmer-muenchen",
    lang: "de",
    city: "München",
    title: "Möbliertes Zimmer München – all-inclusive mieten | coliviq",
    description:
      "Möbliertes Zimmer in München auf Zeit mieten: all-inclusive, mit Anmeldung und ohne Makler. Sofort oder ab Wunschtermin verfügbar.",
    h1: "Möbliertes Zimmer in München",
    intro: [
      "Ein möbliertes Zimmer in München – sofort einziehen, ohne eigene Möbel. coliviq vermietet vollmöblierte Zimmer in Co-Living-WGs, all-inclusive und mit Anmeldung.",
      "Ideal für Wohnen auf Zeit: befristet oder unbefristet, ab sofort oder zum Wunschtermin. Der komplette Vertrag läuft digital.",
    ],
    benefits: [
      "Vollmöbliert und sofort bezugsfertig",
      "Wohnen auf Zeit – befristet oder flexibel",
      "All-inclusive-Miete, ein Betrag pro Monat",
      "Mit Anmeldung und Wohnungsgeberbestätigung",
      "Ohne Makler, digital unterschrieben",
    ],
    faq: [
      {
        question:
          "Kann ich ein möbliertes Zimmer in München auch nur für ein paar Monate mieten?",
        answer:
          "Ja. coliviq bietet Wohnen auf Zeit – von wenigen Monaten bis unbefristet, je nach Zimmer.",
      },
      {
        question: "Ist das Zimmer wirklich komplett möbliert?",
        answer:
          "Ja, inklusive Bett, Schreibtisch, Schrank und voll ausgestatteter Gemeinschaftsküche.",
      },
      {
        question: "Bekomme ich eine Anmeldung?",
        answer:
          "Ja, du erhältst eine Wohnungsgeberbestätigung für die Anmeldung beim Bürgerbüro München.",
      },
      {
        question: "Ab wann kann ich einziehen?",
        answer:
          "Viele Zimmer sind sofort verfügbar, andere ab einem Wunschtermin. Den freien Termin siehst du direkt beim Zimmer.",
      },
    ],
    ctaLabel: "Möblierte Zimmer in München ansehen",
  },
  {
    path: "/wg-zimmer-augsburg",
    lang: "de",
    city: "Augsburg",
    title: "WG-Zimmer Augsburg – möbliert & mit Anmeldung | coliviq",
    description:
      "Freie WG-Zimmer in Augsburg: vollmöbliert, all-inclusive und mit Anmeldung. Ohne Makler und 100 % digital – jetzt WG-Zimmer in Augsburg finden.",
    h1: "WG-Zimmer in Augsburg",
    intro: [
      "WG-Zimmer in Augsburg gesucht? coliviq vermietet vollmöblierte Zimmer in Co-Living-WGs – all-inclusive, mit Anmeldung und ohne Makler.",
      "Perfekt für Studenten, Werkstudenten und Berufseinsteiger: einziehen, anmelden, loslegen – alles digital.",
    ],
    benefits: [
      "Vollmöbliert – startklar ab dem ersten Tag",
      "All-inclusive: Strom, Internet und Nebenkosten inklusive",
      "Mit Anmeldung und Wohnungsgeberbestätigung",
      "Ohne Makler und ohne Provision",
      "Digitaler Vertrag, in Minuten abgeschlossen",
    ],
    faq: [
      {
        question: "Gibt es in Augsburg WG-Zimmer mit Anmeldung?",
        answer:
          "Ja. Zu jedem coliviq-Zimmer in Augsburg gehört eine Wohnungsgeberbestätigung für die Anmeldung beim Bürgeramt.",
      },
      {
        question: "Sind die Zimmer für Studenten geeignet?",
        answer:
          "Ja, unsere WGs sind ideal für Studenten, Werkstudenten und Berufseinsteiger – möbliert und all-inclusive.",
      },
      {
        question: "Was kostet ein WG-Zimmer in Augsburg?",
        answer:
          "Die Miete ist all-inclusive und je Zimmer unterschiedlich. Den aktuellen Preis siehst du direkt beim freien Zimmer.",
      },
      {
        question: "Muss ich einen Makler bezahlen?",
        answer:
          "Nein. Du mietest direkt über coliviq, ganz ohne Makler und Provision.",
      },
    ],
    ctaLabel: "Freie WG-Zimmer in Augsburg ansehen",
  },
];

/** The /rooms filter route a keyword page funnels into. */
export function keywordPageRoomsHref(page: KeywordPage): string {
  return `/rooms/${page.city.toLowerCase()}`;
}

/** Look up a keyword page by its path. */
export function keywordPage(path: string): KeywordPage | undefined {
  return KEYWORD_PAGES.find((p) => p.path === path);
}

/** SeoMeta for a keyword landing page; its FAQ doubles as FAQPage JSON-LD. */
export function keywordPageMeta(page: KeywordPage): SeoMeta {
  return {
    title: page.title,
    description: page.description,
    canonical: absoluteUrl(page.path),
    lang: page.lang,
    image: absoluteUrl(SITE.ogImage),
    jsonLd: [faqLd(page.faq)],
  };
}
